import jsPDF from "jspdf";

/**
 * Dossier de transmission (PDF) — livrable notaire / family office.
 *
 * Rendu PUR : il ne calcule rien. Toutes les valeurs (synthèse, parts, droits,
 * datation) sont fournies par l'appelant via `data`, pour rester la source
 * unique de vérité avec le bloc « Équilibre » affiché à l'écran. Les libellés
 * viennent de `t` (i18n) → le PDF respecte la langue courante.
 *
 * Distingue explicitement les œuvres SANS estimation datée : marquées « à
 * estimer » dans l'inventaire et regroupées dans une section dédiée.
 */
export async function generateTransmissionDossierPDF({ data, t, locale = "fr-FR" }) {
  const doc = new jsPDF();
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 16;
  const contentW = pageW - margin * 2;
  const bottom = pageH - 18;

  const fmt = (n) => Math.round(Number(n) || 0).toLocaleString(locale);
  const eur = (n) => `${fmt(n)} €`;

  let y = margin;

  const ensure = (needed) => {
    if (y + needed > bottom) {
      doc.addPage();
      y = margin;
    }
  };

  const heading = (text, size = 13) => {
    ensure(12);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(size);
    doc.setTextColor(20, 20, 20);
    doc.text(text, margin, y);
    y += size * 0.5 + 3;
    doc.setDrawColor(220, 220, 220);
    doc.line(margin, y, pageW - margin, y);
    y += 5;
  };

  const line = (text, opts = {}) => {
    const { size = 10, color = [60, 60, 60], gap = 5.5, bold = false, x = margin } = opts;
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(size);
    doc.setTextColor(...color);
    const lines = doc.splitTextToSize(text, contentW - (x - margin));
    ensure(lines.length * gap);
    doc.text(lines, x, y);
    y += lines.length * gap;
    return lines.length;
  };

  // --- En-tête -------------------------------------------------------------
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(15, 15, 15);
  doc.text(t("succession.dossier.title"), margin, y);
  y += 8;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(120, 120, 120);
  doc.text("Artkiv", margin, y);
  y += 8;
  doc.setTextColor(60, 60, 60);
  if (data.clientName) {
    doc.text(`${t("succession.dossier.client")} : ${data.clientName}`, margin, y);
    y += 5.5;
  }
  doc.text(
    `${t("succession.dossier.date")} : ${new Date().toLocaleDateString(locale)}`,
    margin,
    y
  );
  y += 10;

  // --- 1. Synthèse patrimoniale -------------------------------------------
  heading(t("succession.dossier.synthesisTitle"));
  line(`${t("succession.balance.collectionValue")} : ${eur(data.collectionValue)}`);
  line(`${t("succession.balance.assigned")} : ${eur(data.assignedValue)}`);
  line(`${t("succession.balance.unassigned")} : ${eur(data.unassignedValue)}`);
  line(t("succession.balance.coverage", { pct: data.coveragePct }), {
    size: 9,
    color: [120, 120, 120],
  });
  line(
    data.baseIsGlobal
      ? t("succession.balance.baseGlobal", { source: data.baseSource })
      : t("succession.balance.baseCollectionOnly"),
    { size: 9, color: [120, 120, 120] }
  );
  y += 4;

  // --- 2. Répartition & fiscalité par héritier ----------------------------
  if (data.heirs.length > 0) {
    heading(
      t(
        data.includeTax
          ? "succession.dossier.heirsTitle"
          : "succession.dossier.heirsTitleNoTax"
      )
    );
    const shareOf = data.baseIsGlobal
      ? t("succession.balance.ofEstate")
      : t("succession.balance.ofCollection");
    data.heirs.forEach((h) => {
      line(data.includeTax ? `${h.name} — ${h.regimeLabel}` : h.name, {
        bold: true,
        gap: 5,
      });
      const shareTxt = `${eur(h.value)} · ${h.pct}% ${shareOf}`;
      const right = !data.includeTax
        ? ""
        : h.exempt
        ? t("succession.tax.exempt")
        : `${t("succession.tax.estimatedLabel")} ~ ${eur(h.tax)}`;
      line(right ? `${shareTxt}  |  ${right}` : shareTxt, {
        size: 9,
        color: [90, 90, 90],
        gap: 6,
      });
    });
    if (data.includeTax) {
      line(`${t("succession.tax.totalLabel")} : ~ ${eur(data.totalTax)}`, {
        bold: true,
        gap: 7,
      });
      line(t("succession.tax.note"), { size: 8, color: [130, 130, 130], gap: 4 });
    }
    line(t("succession.balance.reserveNote"), {
      size: 8,
      color: [130, 130, 130],
      gap: 4,
    });
    y += 4;
  }

  // --- 3. Inventaire des œuvres -------------------------------------------
  heading(t("succession.dossier.inventoryTitle"));
  const none = t("succession.dossier.none");
  data.items.forEach((it) => {
    ensure(14);
    line(`${it.n}. ${it.title} — ${it.artist}`, { bold: true, gap: 5 });
    const estim = it.dated
      ? `${t("succession.valuation.source.estimation")} ${t("succession.valuation.asOf", {
          date: it.dateStr,
        })}`
      : t("succession.dossier.toEstimate");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(90, 90, 90);
    const detail = `${t("succession.dossier.value")} : ${eur(it.value)}  ·  ${estim}  ·  ${t(
      "succession.dossier.heirs"
    )} : ${it.heirsStr || none}`;
    const dl = doc.splitTextToSize(detail, contentW);
    ensure(dl.length * 5 + 2);
    doc.text(dl, margin, y);
    y += dl.length * 5 + 2;

    // Provenance + localisation (si renseignées) — valeur notariale.
    if (it.provenance || it.location) {
      const meta = [
        it.provenance
          ? `${t("succession.dossier.provenance")} : ${it.provenance}`
          : null,
        it.location ? `${t("succession.dossier.location")} : ${it.location}` : null,
      ]
        .filter(Boolean)
        .join("  ·  ");
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(120, 120, 120);
      const ml = doc.splitTextToSize(meta, contentW);
      ensure(ml.length * 4.5 + 2);
      doc.text(ml, margin, y);
      y += ml.length * 4.5;
    }
    y += 3;
  });
  y += 4;

  // --- 4. Œuvres à faire estimer (section dédiée) -------------------------
  if (data.undatedItems.length > 0) {
    heading(t("succession.dossier.toEstimateTitle"));
    line(
      t("succession.dossier.toEstimateIntro", { count: data.undatedItems.length }),
      { size: 9, color: [90, 90, 90], gap: 6 }
    );
    data.undatedItems.forEach((it, i) => {
      line(`${i + 1}. ${it.title} — ${it.artist}`, {
        size: 9,
        color: [60, 60, 60],
        gap: 5,
      });
    });
    y += 4;
  }

  // --- Mentions légales ----------------------------------------------------
  heading(t("succession.dossier.legalTitle"), 11);
  line(t("succession.legal.notice"), { size: 8, color: [120, 120, 120], gap: 4 });

  // --- Pieds de page (toutes pages) ---------------------------------------
  const total = doc.internal.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.setDrawColor(220, 220, 220);
    doc.line(margin, pageH - 13, pageW - margin, pageH - 13);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(150, 150, 150);
    doc.text(t("succession.dossier.confidential"), margin, pageH - 8);
    doc.text(
      t("succession.dossier.page", { n: i, total }),
      pageW - margin,
      pageH - 8,
      { align: "right" }
    );
  }

  // --- Téléchargement ------------------------------------------------------
  const safe = (data.clientName || "artkiv").replace(/[^a-zA-Z0-9_-]/g, "_");
  const stamp = new Date().toISOString().slice(0, 10);
  doc.save(`dossier_transmission_${safe}_${stamp}.pdf`);
}
